/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { EnvironmentService } from './environment.service';
import { Account } from './models/account.model';
import rg4js from 'raygun4js';
import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root'
})
export class RaygunService {
  private readonly redactedValue = '[removed by filter]';
  private readonly sensitivePayloadKeys = [
    'password',
    'newPassword',
    'confirmPassword'
  ];
  private readonly sensitiveUrlParams = [
    ...this.sensitivePayloadKeys
  ];

  private isInitialized = false;

  constructor(
    private environmentService: EnvironmentService,
    private sharedService: SharedService
  ) {}

  initialize(): void {
    const environment = this.environmentService.getEnvironment();

    if (window.location.hostname === 'localhost') {
      console.log('Raygun is disabled for localhost');

      return;
    }

    if (this.isInitialized) {
      return;
    }

    try {
      rg4js('apiKey', environment.raygunApiKey);
      rg4js('enableCrashReporting', true);
      rg4js('enablePulse', false);
      rg4js('setFilterScope', 'all');
      rg4js('filterSensitiveData', this.sensitivePayloadKeys);
      rg4js('onBeforeSend', (payload) => this.sanitizePayload(payload));

      rg4js('options', {
        debugMode: !environment.production,
        allowInsecureSubmissions: !environment.production,
        excludedHostnames: [],
        excludedUserAgents: [],
        disableAnonymousUserTracking: false,
        disableErrorTracking: false,
        apiEndpoint: 'https://api.raygun.com',
        captureUnhandledRejections: true
      });

      rg4js('setVersion', '1.0.0');
      rg4js('withTags', [environment.production ? 'production' : 'uat']);

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Raygun:', error);
    }
  }

  sendError(error: Error): void {
    if (!this.isInitialized) {
      console.warn('Raygun not initialized. Cannot send error.');

      return;
    }

    try {
      this.ensureUserContext();
      rg4js('send', error);
    } catch (e) {
      console.error('Failed to send error to Raygun:', e);
    }
  }

  setUser(identifier: string, isAnonymous = false, email?: string, fullName?: string): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      rg4js('setUser', {
        identifier: identifier,
        isAnonymous: isAnonymous,
        email: email,
        fullName: fullName
      });
    } catch (error) {
      console.error('Failed to set Raygun user:', error);
    }
  }

  setUserFromAccount(account: Account): void {
    if (!account) {
      return;
    }

    const fullName = `${account.firstname} ${account.lastname}`.trim();
    this.setUser(
      account.id.toString(),
      false,
      account.email,
      fullName
    );

    this.addUserCustomData(account);
  }

  addCustomData(key: string, value: any): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      rg4js('withCustomData', { [key]: value });
    } catch (error) {
      console.error('Failed to add custom data to Raygun:', error);
    }
  }

  private addUserCustomData(account: Account): void {
    try {
      const userData = {
        user_id: account.id,
        user_email: account.email,
        user_firstname: account.firstname,
        user_lastname: account.lastname,
        user_company: account.companyName,
        user_department: account.department
      };

      this.addCustomData('user_data', userData);
    } catch (error) {
      console.error('Failed to add user custom data to Raygun:', error);
    }
  }

  private ensureUserContext(): void {
    try {
      const account = this.sharedService.getAccountData();

      if (account) {
        this.setUserFromAccount(account);
      } else {
        this.setUser('anonymous', true);
      }
    } catch (error) {
      console.warn('Failed to ensure user context for Raygun:', error);
      this.setUser('anonymous', true);
    }
  }

  private sanitizePayload<T>(payload: T): T {
    return this.sanitizeValue(payload) as T;
  }

  private sanitizeValue(value: unknown, key?: string): unknown {
    if (this.isSensitivePayloadKey(key)) {
      return this.redactedValue;
    }

    if (typeof value === 'string') {
      return this.sanitizeUrlParams(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeValue(item));
    }

    if (value && Object.prototype.toString.call(value) === '[object Object]') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
        (sanitized, [entryKey, entryValue]) => {
          sanitized[entryKey] = this.sanitizeValue(entryValue, entryKey);

          return sanitized;
        },
        {}
      );
    }

    return value;
  }

  private sanitizeUrlParams(value: string): string {
    return this.sensitiveUrlParams.reduce((sanitizedValue, param) => {
      const paramPattern = new RegExp(`([?&]${param}=)[^&#]*`, 'gi');

      return sanitizedValue.replace(paramPattern, `$1${this.redactedValue}`);
    }, value);
  }

  private isSensitivePayloadKey(key?: string): boolean {
    return !!key && this.sensitivePayloadKeys.some(
      (sensitiveKey) => sensitiveKey.toLowerCase() === key.toLowerCase()
    );
  }
}
